import { Box } from "@mui/material";
import { Container, SearchBar, ServiceCard } from "../components";
import { useDimensions, useTheme } from "../hooks";
import { useEffect, useMemo, useState } from "react";
import { CorporateService } from "../models/Service";
import { useQueryGroupTheme, useQueryService } from "../api/queries";
import { getCorporateType, getStorageToken } from "../utils";
import ServiceSkeleton from "../components/serviceSkeleton";
import { useTranslation } from "react-i18next";

// Module-level so the empty case has ONE identity. A `= []` default in the
// destructure below builds a new array on every render, which changes the
// memo's identity, which re-runs the effect, which sets state, which renders
// again -- the catalogue spin-locked before it had any services to show.
const NO_SERVICES: CorporateService[] = [];

const ServiceList = () => {
  const { isSmall, isMedium } = useDimensions();
  const { theme } = useTheme();
  const token = getStorageToken();
  const [filteredServices, setFilteredServices] = useState<CorporateService[]>(
    []
  );
  const [searchText, setSearchText] = useState("");
  const { t } = useTranslation();
  const { data, isLoading: isLoadingCorporateThemeDetails } =
    useQueryGroupTheme({
      id: theme?.id,
      groupType: getCorporateType(theme?.url_type),
      enable: !!theme?.id,
      authorisation: token || "",
    });

  const corporateId = data?.id || "";

  const { data: serviceList, isLoading: isLoadingServices } = useQueryService({
    corporateId,
    enabled: !!corporateId,
    authorisation: token || "",
  });

  const services = useMemo(() => {
    return (serviceList as CorporateService[]) || NO_SERVICES;
  }, [serviceList]);

  const handleSearch = (event: React.FormEvent<HTMLInputElement>) => {
    const searchText = event.currentTarget.value.toLowerCase();
    if (!searchText) {
      setFilteredServices(services);
      return;
    }
    setSearchText(searchText);

    const filtered = services?.filter(service =>
      service.service_name.toLowerCase().includes(searchText)
    );

    setFilteredServices(filtered);
  };

  useEffect(() => {
    setFilteredServices(services);
  }, [services]);
  return (
    <Container
      bordered
      justifyContent="center"
      alignItems="center"
      sx={{
        borderColor: theme?.color_primary,
        height: "55vh",
        borderRadius: 4,
        backgroundColor: theme?.light_gray,
      }}
    >
      <Box mx={isSmall ? 5 : isMedium ? 10 : 25} pt={5}>
        <SearchBar
          placeHolder={t("homepage.search_placeholder")}
          display={"flex"}
          onChange={handleSearch}
        />
      </Box>
      <Box
        display={isSmall ? "block" : "flex"}
        justifyContent={"flex-start"}
        flexWrap={"wrap"}
        mx={isSmall ? 5 : isMedium ? 10 : 30}
        mt={5}
        data-testid="service-grid"
      >
        {isLoadingCorporateThemeDetails || isLoadingServices ? (
          <>
            <ServiceSkeleton />
            <ServiceSkeleton />
            <ServiceSkeleton />
          </>
        ) : (
          <>
            {filteredServices.map(service => (
              <ServiceCard service={service} key={service.id} />
            ))}
          </>
        )}
      </Box>
    </Container>
  );
};

export default ServiceList;
